/**
 * Quality tiers.
 *
 * Two jobs: pick a sensible starting tier from a cheap device probe, then watch
 * real frame times and step down if we guessed too high. Stepping down is
 * one-way within a session - oscillating between tiers is more distracting than
 * running a notch below optimal.
 *
 * Target: 60fps on desktop, a locked 30fps on mid-tier mobile.
 */

import { tuning, type QualityTierSettings } from '@/core/tuning';

export type QualityTierName = 'low' | 'medium' | 'high';

const TIER_ORDER: readonly QualityTierName[] = ['low', 'medium', 'high'];

export interface DeviceProbe {
  isMobile: boolean;
  devicePixelRatio: number;
  cores: number;
  /** Unmasked GL renderer string when available - the best signal we can get. */
  glRenderer: string;
}

export function probeDevice(canvas?: HTMLCanvasElement): DeviceProbe {
  const ua = navigator.userAgent;
  // maxTouchPoints catches iPads reporting a desktop UA, which the UA string alone misses.
  const isMobile =
    /Android|iPhone|iPad|iPod|Mobile|Silk/i.test(ua) ||
    (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua));

  let glRenderer = '';
  try {
    const probeCanvas = canvas ?? document.createElement('canvas');
    const gl = probeCanvas.getContext('webgl2') ?? probeCanvas.getContext('webgl');
    if (gl) {
      const ext = gl.getExtension('WEBGL_debug_renderer_info');
      glRenderer = ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : '';
    }
  } catch {
    // Probing is best-effort. A blocked extension just means we fall back to
    // the mobile/cores heuristic.
  }

  return {
    isMobile,
    devicePixelRatio: window.devicePixelRatio || 1,
    cores: navigator.hardwareConcurrency || 2,
    glRenderer,
  };
}

/**
 * Starting tier. Conservative on mobile by design: it is far better to boot at
 * 'low' and look flat than to boot at 'high' and stutter, because a 7-year-old
 * reads stutter as "broken" and flat shading as "the style".
 */
export function pickInitialTier(probe: DeviceProbe): QualityTierName {
  if (probe.isMobile) {
    // Known-weak mobile GPU families.
    if (/Mali-4|Adreno \(TM\) [1-4]|PowerVR SGX/i.test(probe.glRenderer)) return 'low';
    return probe.cores >= 6 ? 'medium' : 'low';
  }
  // Software rasterizers must never be given shadows.
  if (/SwiftShader|Software|Basic Render/i.test(probe.glRenderer)) return 'low';
  return probe.cores >= 4 ? 'high' : 'medium';
}

export function settingsFor(tier: QualityTierName): QualityTierSettings {
  return tuning.quality.tiers[tier];
}

export type TierChangeListener = (tier: QualityTierName, settings: QualityTierSettings) => void;

export class QualityManager {
  private tier: QualityTierName;
  private slowFrames = 0;
  private listeners: TierChangeListener[] = [];
  readonly probe: DeviceProbe;

  constructor(canvas?: HTMLCanvasElement) {
    this.probe = probeDevice(canvas);
    this.tier = pickInitialTier(this.probe);
  }

  get current(): QualityTierName {
    return this.tier;
  }

  get settings(): QualityTierSettings {
    return settingsFor(this.tier);
  }

  /** Target frame budget in seconds for this device class. */
  get frameBudget(): number {
    const fps = this.probe.isMobile
      ? tuning.quality.targetFpsMobile
      : tuning.quality.targetFpsDesktop;
    return 1 / fps;
  }

  onChange(listener: TierChangeListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /**
   * Call once per rendered frame with the real frame delta. Counts sustained
   * budget overruns and steps down after `downgradeAfterSlowFrames` of them.
   * A single slow frame (a GC pause, a level load) must never trigger a change.
   */
  sample(realDt: number): void {
    // 1.25x budget: allow genuine jitter without treating it as a trend.
    if (realDt > this.frameBudget * 1.25) {
      this.slowFrames++;
      if (this.slowFrames >= tuning.quality.downgradeAfterSlowFrames) this.downgrade();
    } else if (this.slowFrames > 0) {
      // Decay rather than reset, so alternating fast/slow frames still count.
      this.slowFrames--;
    }
  }

  private downgrade(): void {
    const index = TIER_ORDER.indexOf(this.tier);
    if (index <= 0) {
      this.slowFrames = 0;
      return;
    }
    this.setTier(TIER_ORDER[index - 1] as QualityTierName);
    console.info(`[cat-game] frame budget missed, dropping quality to "${this.tier}"`);
  }

  /** Manual override, for the settings menu and the debug HUD. */
  setTier(tier: QualityTierName): void {
    if (tier === this.tier) return;
    this.tier = tier;
    this.slowFrames = 0;
    for (const listener of this.listeners) listener(this.tier, this.settings);
  }
}
